import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Plus, Edit, Trash2, Search, Key } from 'lucide-react';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [passwordUser, setPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({ email: '', name: '', password: '', role_id: '' });

  useEffect(() => { fetchUsers(); fetchRoles(); }, []);

  const fetchUsers = async () => {
    try { const res = await api.get('/admin/users'); setUsers(res.data); }
    catch (err) { console.error('Failed to fetch users:', err); }
    finally { setLoading(false); }
  };

  const fetchRoles = async () => {
    try { const res = await api.get('/admin/roles'); setRoles(res.data); }
    catch (err) { console.error('Failed to fetch roles:', err); }
  };

  const getRoleName = (roleId) => roles.find(r => r.id === roleId)?.name || 'Unknown';

  const openCreate = () => {
    setEditingUser(null);
    setFormData({ email: '', name: '', password: '', role_id: '' });
    setShowModal(true);
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setFormData({ email: user.email, name: user.name, password: '', role_id: user.role_id });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        const updateData = { name: formData.name, role_id: formData.role_id };
        await api.put(`/admin/users/${editingUser.id}`, updateData);
      } else {
        await api.post('/admin/users', formData);
      }
      setShowModal(false);
      fetchUsers();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to save user'); }
  };

  const deleteUser = async (id, name) => {
    if (!window.confirm(`Delete user "${name}"?`)) return;
    try { await api.delete(`/admin/users/${id}`); fetchUsers(); }
    catch (err) { alert(err.response?.data?.detail || 'Failed to delete user'); }
  };

  const openChangePassword = (user) => {
    setPasswordUser(user);
    setNewPassword('');
    setShowPasswordModal(true);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { alert('Password must be at least 6 characters'); return; }
    try {
      await api.post(`/admin/users/${passwordUser.id}/change-password`, { user_id: passwordUser.id, new_password: newPassword });
      setShowPasswordModal(false);
      alert('Password changed successfully');
    } catch (err) { alert(err.response?.data?.detail || 'Failed to change password'); }
  };

  const toggleStatus = async (user) => {
    const newStatus = user.status === 'active' ? 'inactive' : 'active';
    try {
      await api.put(`/admin/users/${user.id}`, { status: newStatus });
      fetchUsers();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to update status'); }
  };

  const filtered = users.filter(u =>
    u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div data-testid="users-page">
      <div className="flex items-center justify-between mb-4">
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Users</h1>
        <button onClick={openCreate} className="btn btn-primary" data-testid="add-user-btn"><Plus size={16} /> Add User</button>
      </div>

      <div className="card mb-4">
        <div className="form-group" style={{ marginBottom: 0 }}>
          <div style={{ position: 'relative' }}>
            <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
            <input type="text" className="form-input" placeholder="Search by name or email..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ paddingLeft: '40px' }} data-testid="search-input" />
          </div>
        </div>
      </div>

      <div className="card">
        {loading ? <div data-testid="loading-indicator">Loading users...</div> : (
          <div className="table-container">
            <table>
              <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Created</th><th>Actions</th></tr></thead>
              <tbody>
                {filtered.map(user => (
                  <tr key={user.id} data-testid={`user-row-${user.email}`}>
                    <td>{user.name}</td>
                    <td>{user.email}</td>
                    <td>{getRoleName(user.role_id)}</td>
                    <td>
                      <span
                        className={`badge ${user.status === 'active' ? 'badge-success' : 'badge-danger'}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => toggleStatus(user)}
                        title="Click to toggle status"
                        data-testid={`status-${user.email}`}
                      >
                        {user.status}
                      </span>
                    </td>
                    <td className="text-sm">{new Date(user.created_at).toLocaleDateString()}</td>
                    <td>
                      <div className="flex gap-2">
                        <button onClick={() => openEdit(user)} className="btn btn-ghost btn-sm" title="Edit" data-testid={`edit-btn-${user.email}`}><Edit size={14} /></button>
                        <button onClick={() => openChangePassword(user)} className="btn btn-ghost btn-sm" title="Change Password" data-testid={`password-btn-${user.email}`}><Key size={14} /></button>
                        <button onClick={() => deleteUser(user.id, user.name)} className="btn btn-ghost btn-sm" title="Delete" data-testid={`delete-btn-${user.email}`}><Trash2 size={14} color="#DC2626" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create / Edit User Modal */}
      {showModal && (
        <div className="modal-overlay" data-testid="user-modal">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>{editingUser ? 'Edit User' : 'Add User'}</h2>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Name *</label>
                  <input type="text" className="form-input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="name-input" />
                </div>
                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <input type="email" className="form-input" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required disabled={!!editingUser} data-testid="email-input" />
                </div>
                {!editingUser && (
                  <div className="form-group">
                    <label className="form-label">Password *</label>
                    <input type="password" className="form-input" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required data-testid="password-input" />
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">Role *</label>
                  <select className="form-select" value={formData.role_id} onChange={(e) => setFormData({ ...formData, role_id: e.target.value })} required data-testid="role-select">
                    <option value="">Select role...</option>
                    {roles.map(role => <option key={role.id} value={role.id}>{role.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-user-btn">{editingUser ? 'Update User' : 'Add User'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showPasswordModal && passwordUser && (
        <div className="modal-overlay" data-testid="password-modal">
          <div className="modal-content" style={{ maxWidth: '400px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>Change Password</h2>
            </div>
            <form onSubmit={handleChangePassword}>
              <div className="modal-body">
                <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '16px' }}>Changing password for <strong>{passwordUser.name}</strong> ({passwordUser.email})</p>
                <div className="form-group">
                  <label className="form-label">New Password *</label>
                  <input type="password" className="form-input" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={6} data-testid="new-password-input" />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowPasswordModal(false)} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-password-btn">Change Password</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
